import {
  authenticatedPost,
  authenticatedApiCall,
} from '../utils/authenticatedFetch';
import { configs } from '../configs';

export interface AssistantMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface AssistantChatRequest {
  message: string;
  context?: {
    page?: string;
    userId?: string;
    roles?: string[];
    locale?: string;
  };
  screenshot?: string;
  history?: AssistantMessage[]; // Conversation history for context
  chatModel?: string; // Optional chat model override
  // Note: Role simulation is handled globally via X-Simulated-Roles header
}

export interface AssistantChatResponse {
  response: string;
  actions?: Array<{
    type: string;
    entity: string;
    status: string;
    data: any;
  }>;
  suggestions?: string[];
}

export interface AssistantCapabilities {
  canCreate: string[];
  canView: string[];
}

export interface SummarizeRequest {
  messages: AssistantMessage[];
  currentUserId: string;
}

export interface SummarizeResponse {
  summary: string;
  tokenCount: number;
  originalTokenCount: number;
}

export interface PolicyMetadataExtractionRequest {
  content: string;
}

export interface PolicyMetadataExtractionResponse {
  title: string;
  description?: string;
  applicableProcesses: string[];
  tags: string[];
  priority?: number;
}

export interface PolicyContentRewriteRequest {
  content: string;
  language?: string; // Optional: specify language for better rewriting
}

export interface PolicyContentRewriteResponse {
  rewrittenContent: string;
  improvements?: string[]; // Optional: list of improvements made
}

export interface TaskEnhancementRequest {
  currentTask?: Partial<{
    title: string;
    description: string;
    status: string;
    priority: string;
    assigneeId: string;
    dueDate: string;
    estimatedHours: number;
    tags: string[];
  }>;
  userPrompt?: string; // Optional user instruction for refinement
  locale?: string; // User's selected language (e.g., 'en', 'vi')
}

export interface TaskEnhancementResponse {
  title?: string;
  description?: string;
  status?: string;
  priority?: string;
  assigneeId?: string;
  dueDate?: string;
  estimatedHours?: number;
  tags?: string[];
  suggestions?: string[]; // Additional suggestions for improvement
}

class AssistantService {
  private baseUrl = `${configs.apiRoot}/assistant`;

  /**
   * Send a message to the assistant
   */
  async chat(request: AssistantChatRequest): Promise<AssistantChatResponse> {
    return authenticatedPost<AssistantChatResponse>(
      `${this.baseUrl}/chat`,
      request
    );
  }

  /**
   * Summarize conversation history
   */
  async summarize(request: SummarizeRequest): Promise<SummarizeResponse> {
    return authenticatedPost<SummarizeResponse>(
      `${this.baseUrl}/summarize`,
      request
    );
  }

  /**
   * Get user capabilities
   */
  async getCapabilities(): Promise<AssistantCapabilities> {
    return authenticatedApiCall<AssistantCapabilities>(
      `${this.baseUrl}/capabilities`
    );
  }

  /**
   * Execute an action (approve/reject)
   */
  async executeAction(
    actionId: string,
    confirmation: boolean,
    additionalData?: any
  ): Promise<any> {
    return authenticatedPost<any>(`${this.baseUrl}/execute-action`, {
      actionId,
      confirmation,
      additionalData,
    });
  }

  /**
   * Extract policy metadata from content using AI
   */
  async extractPolicyMetadata(
    content: string
  ): Promise<PolicyMetadataExtractionResponse> {
    const prompt = `You are a policy management assistant. Analyze the following policy content and extract structured metadata. Return ONLY a valid JSON object with the following structure:
{
  "title": "A concise, descriptive title (max 100 characters)",
  "description": "A brief summary/description (optional, max 200 characters)",
  "applicableProcesses": ["array", "of", "business", "process", "names"],
  "tags": ["array", "of", "categorization", "tags"],
  "priority": 0
}

IMPORTANT LANGUAGE GUIDELINES:
- Detect the language of the policy content (Vietnamese, English, French, etc.)
- Title: MUST be in the SAME language as the policy content. Extract or create a clear, concise title that summarizes the policy.
- Description: MUST be in the SAME language as the policy content. Provide a brief summary if the content is complex, otherwise can be empty string.
- Applicable Processes: 
  * If policy content is in English: Can be in English only. Examples: "order processing", "refund handling", "customer service", "inventory management", "staff management", "payment processing".
  * If policy content is NOT in English (e.g., Vietnamese): MUST be BILINGUAL - provide both the native language AND English. Format: ["native language / English", ...]. Examples for Vietnamese content: ["xử lý đơn hàng / order processing", "xử lý hoàn tiền / refund handling", "dịch vụ khách hàng / customer service", "quản lý kho / inventory management", "quản lý nhân viên / staff management", "xử lý thanh toán / payment processing"]. Extract 2-5 relevant process names.
- Tags:
  * If policy content is in English: Can be in English only. Examples: "compliance", "safety", "urgent", "refund", "customer-service", "operational". Use lowercase with hyphens for multi-word tags.
  * If policy content is NOT in English (e.g., Vietnamese): MUST be BILINGUAL - provide both the native language AND English. Format: ["native language / English", ...]. Examples for Vietnamese content: ["tuân thủ / compliance", "an toàn / safety", "khẩn cấp / urgent", "hoàn tiền / refund", "dịch vụ khách hàng / customer-service", "vận hành / operational"]. Extract 3-7 relevant categorization tags.
- Priority: Assign a priority number (0-10, where 0 is default/normal, 10 is critical)

Policy content to analyze:
${content}

Return ONLY the JSON object, no other text.`;

    try {
      const response = await this.chat({
        message: prompt,
        context: {
          page: 'policy-management',
        },
      });

      // Parse the JSON response
      const responseText = response.response.trim();

      // Try to extract JSON from the response (in case LLM adds extra text)
      // Look for JSON object boundaries
      let jsonText = responseText;

      // Try to find JSON object
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        jsonText = jsonMatch[0];
      } else {
        // If no JSON found, try to parse the whole response
        jsonText = responseText;
      }

      // Clean up common LLM artifacts
      jsonText = jsonText
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .trim();

      let extracted: PolicyMetadataExtractionResponse;
      try {
        extracted = JSON.parse(jsonText) as PolicyMetadataExtractionResponse;
      } catch (parseError) {
        // If parsing fails, try to extract fields using regex as fallback
        console.warn(
          'Failed to parse JSON, attempting regex extraction:',
          parseError
        );
        const titleMatch = jsonText.match(/"title"\s*:\s*"([^"]+)"/);
        const descMatch = jsonText.match(/"description"\s*:\s*"([^"]*)"/);
        const processesMatch = jsonText.match(
          /"applicableProcesses"\s*:\s*\[([^\]]+)\]/
        );
        const tagsMatch = jsonText.match(/"tags"\s*:\s*\[([^\]]+)\]/);
        const priorityMatch = jsonText.match(/"priority"\s*:\s*(\d+)/);

        extracted = {
          title: titleMatch ? titleMatch[1] : 'Untitled Policy',
          description: descMatch ? descMatch[1] : '',
          applicableProcesses: processesMatch
            ? processesMatch[1]
                .split(',')
                .map((p) => p.trim().replace(/^["']|["']$/g, ''))
                .filter((p) => p.length > 0)
            : [],
          tags: tagsMatch
            ? tagsMatch[1]
                .split(',')
                .map((t) => t.trim().replace(/^["']|["']$/g, ''))
                .filter((t) => t.length > 0)
            : [],
          priority: priorityMatch ? parseInt(priorityMatch[1], 10) : 0,
        };
      }

      // Validate and normalize the response
      return {
        title: extracted.title || 'Untitled Policy',
        description: extracted.description || '',
        applicableProcesses: Array.isArray(extracted.applicableProcesses)
          ? extracted.applicableProcesses
          : [],
        tags: Array.isArray(extracted.tags) ? extracted.tags : [],
        priority:
          typeof extracted.priority === 'number' ? extracted.priority : 0,
      };
    } catch (error: any) {
      console.error('Error extracting policy metadata:', error);
      throw new Error(
        `Failed to extract policy metadata: ${error.message || 'Unknown error'}`
      );
    }
  }

  /**
   * Rewrite policy content to make it clearer and easier to follow
   */
  async rewritePolicyContent(
    content: string,
    language?: string
  ): Promise<PolicyContentRewriteResponse> {
    const languageHint = language
      ? `The content is in ${language}. Maintain the same language in the rewritten version.`
      : 'Detect the language of the content and maintain the same language in the rewritten version.';

    const prompt = `You are a policy writing assistant. Rewrite the following policy content to make it clearer, more structured, and easier to follow while preserving all the original meaning and requirements.

${languageHint}

Guidelines for rewriting:
- Maintain the same language as the original content
- Improve clarity and readability
- Use clear headings and structure if appropriate
- Break down complex sentences into simpler ones
- Ensure all important information is preserved
- Make it more professional and easier to understand
- Keep the same tone and formality level

Original policy content:
${content}

Return ONLY a JSON object with this structure:
{
  "rewrittenContent": "The improved, rewritten content",
  "improvements": ["List of key improvements made", "e.g., 'Simplified complex sentences'", "e.g., 'Added clear structure with headings'"]
}

Return ONLY the JSON object, no other text.`;

    try {
      const response = await this.chat({
        message: prompt,
        context: {
          page: 'policy-management',
        },
      });

      // Parse the JSON response
      const responseText = response.response.trim();

      // Try to extract JSON from the response
      let jsonText = responseText;
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        jsonText = jsonMatch[0];
      }

      // Clean up common LLM artifacts
      jsonText = jsonText
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .trim();

      let extracted: PolicyContentRewriteResponse;
      try {
        extracted = JSON.parse(jsonText) as PolicyContentRewriteResponse;
      } catch (parseError) {
        // If parsing fails, use the response as rewritten content
        console.warn(
          'Failed to parse JSON, using response as rewritten content:',
          parseError
        );
        extracted = {
          rewrittenContent: responseText,
          improvements: [],
        };
      }

      // Validate and normalize the response
      return {
        rewrittenContent: extracted.rewrittenContent || content,
        improvements: Array.isArray(extracted.improvements)
          ? extracted.improvements
          : [],
      };
    } catch (error: any) {
      console.error('Error rewriting policy content:', error);
      throw new Error(
        `Failed to rewrite policy content: ${error.message || 'Unknown error'}`
      );
    }
  }

  /**
   * Enhance or autofill task fields using AI
   */
  async enhanceTask(
    request: TaskEnhancementRequest
  ): Promise<TaskEnhancementResponse> {
    const currentTask = request.currentTask || {};
    const userPrompt = request.userPrompt || '';
    const locale = request.locale || 'en';

    // Map locale codes to language names for better AI understanding
    const languageMap: Record<string, string> = {
      en: 'English',
      vi: 'Vietnamese',
      fr: 'French',
      es: 'Spanish',
      de: 'German',
      ja: 'Japanese',
      ko: 'Korean',
      zh: 'Chinese',
    };
    const language = languageMap[locale] || 'English';
    const isVietnamese = locale === 'vi';
    const isEnglish = locale === 'en';

    // Language-specific instructions
    const languageInstruction = isVietnamese
      ? `IMPORTANT: Generate ALL content (title, description, tags, suggestions) in Vietnamese. Use Vietnamese language for all text fields.`
      : isEnglish
      ? `IMPORTANT: Generate ALL content (title, description, tags, suggestions) in English.`
      : `IMPORTANT: Generate ALL content (title, description, tags, suggestions) in ${language}.`;

    // Build a prompt that includes current task data and asks AI to enhance/complete it
    const hasTitleOnly = !userPrompt && currentTask.title && !currentTask.description && (!currentTask.tags || currentTask.tags.length === 0);
    const mode = userPrompt 
      ? `follow this instruction: "${userPrompt}"`
      : hasTitleOnly
      ? 'autofill all task fields (description, priority, estimated hours, tags, etc.) based on the title provided. The title is the main source of information, so extract all necessary details from it.'
      : 'autofill or refine all fields based on the provided context';
    
    const prompt = `You are a task management assistant. Analyze the following task information and ${mode}.

${languageInstruction}

Current task data:
${JSON.stringify(currentTask, null, 2)}

Available task statuses: todo, in_progress, review, done, blocked
Available task priorities: low, medium, high, urgent

${userPrompt 
  ? `User instruction: ${userPrompt}` 
  : hasTitleOnly
  ? 'Since only the title is provided, use it as the primary source to autofill ALL other fields: generate a detailed description, suggest appropriate priority, estimate hours, add relevant tags, and set a reasonable status. Extract all meaningful information from the title to create a complete task.'
  : 'Please autofill or refine ALL fields. If a field is already filled, you can improve it. If a field is empty, generate appropriate content based on the title, description, or other provided fields.'}

CRITICAL LANGUAGE REQUIREMENT:
The user has selected ${language} as their interface language. ALL generated content MUST be in ${language}, regardless of the language of any existing content in the task data above. If existing content is in a different language, translate it to ${language} while enhancing it.

Language requirements:
- Title: MUST be in ${language}
- Description: MUST be in ${language}
- Tags: MUST be in ${language}${isVietnamese ? ' (can use English technical terms if commonly used, but prefer Vietnamese)' : ''}
- Suggestions: MUST be in ${language}
- Status and Priority values: Use the English enum values (todo, in_progress, etc. for status; low, medium, high, urgent for priority) but ensure all user-facing text content is in ${language}

For dates, use ISO 8601 format (YYYY-MM-DD).
For estimated hours, provide a reasonable number (e.g., 2, 4, 8, 16, 40).
For tags, extract 2-5 relevant tags based on the task content in ${language}.
For priority, assess based on urgency and importance.
For status, use appropriate status (todo for new tasks unless context suggests otherwise).

Return ONLY a valid JSON object with this structure (only include fields that should be updated):
{
  "title": "Enhanced or refined title in ${language}",
  "description": "Enhanced or refined description in ${language}",
  "status": "todo|in_progress|review|done|blocked",
  "priority": "low|medium|high|urgent",
  "dueDate": "YYYY-MM-DD (only if date can be reasonably estimated)",
  "estimatedHours": 0,
  "tags": ["tag1 in ${language}", "tag2 in ${language}"],
  "suggestions": ["Optional suggestion 1 in ${language}", "Optional suggestion 2 in ${language}"]
}

Return ONLY the JSON object, no other text.`;

    try {
      const response = await this.chat({
        message: prompt,
        context: {
          page: 'project-management',
          locale: locale,
        },
      });

      // Parse the JSON response
      const responseText = response.response.trim();

      // Try to extract JSON from the response
      let jsonText = responseText;
      const jsonMatch = responseText.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        jsonText = jsonMatch[0];
      }

      // Clean up common LLM artifacts
      jsonText = jsonText
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .trim();

      let enhanced: TaskEnhancementResponse;
      try {
        enhanced = JSON.parse(jsonText) as TaskEnhancementResponse;
      } catch (parseError) {
        console.warn(
          'Failed to parse JSON, attempting regex extraction:',
          parseError
        );
        // Fallback: try to extract fields using regex
        const titleMatch = jsonText.match(/"title"\s*:\s*"([^"]+)"/);
        const descMatch = jsonText.match(/"description"\s*:\s*"([^"]*)"/);
        const statusMatch = jsonText.match(/"status"\s*:\s*"([^"]+)"/);
        const priorityMatch = jsonText.match(/"priority"\s*:\s*"([^"]+)"/);
        const dueDateMatch = jsonText.match(/"dueDate"\s*:\s*"([^"]+)"/);
        const hoursMatch = jsonText.match(/"estimatedHours"\s*:\s*(\d+)/);
        const tagsMatch = jsonText.match(/"tags"\s*:\s*\[([^\]]+)\]/);
        const suggestionsMatch = jsonText.match(
          /"suggestions"\s*:\s*\[([^\]]+)\]/
        );

        enhanced = {
          ...(titleMatch && { title: titleMatch[1] }),
          ...(descMatch && { description: descMatch[1] }),
          ...(statusMatch && { status: statusMatch[1] }),
          ...(priorityMatch && { priority: priorityMatch[1] }),
          ...(dueDateMatch && { dueDate: dueDateMatch[1] }),
          ...(hoursMatch && {
            estimatedHours: parseInt(hoursMatch[1], 10),
          }),
          ...(tagsMatch && {
            tags: tagsMatch[1]
              .split(',')
              .map((t) => t.trim().replace(/^["']|["']$/g, ''))
              .filter((t) => t.length > 0),
          }),
          ...(suggestionsMatch && {
            suggestions: suggestionsMatch[1]
              .split(',')
              .map((s) => s.trim().replace(/^["']|["']$/g, ''))
              .filter((s) => s.length > 0),
          }),
        };
      }

      // Validate and normalize the response
      return {
        ...(enhanced.title && { title: enhanced.title }),
        ...(enhanced.description !== undefined && {
          description: enhanced.description,
        }),
        ...(enhanced.status && {
          status: enhanced.status as
            | 'todo'
            | 'in_progress'
            | 'review'
            | 'done'
            | 'blocked',
        }),
        ...(enhanced.priority && {
          priority: enhanced.priority as 'low' | 'medium' | 'high' | 'urgent',
        }),
        ...(enhanced.dueDate && { dueDate: enhanced.dueDate }),
        ...(enhanced.estimatedHours !== undefined && {
          estimatedHours: enhanced.estimatedHours,
        }),
        ...(enhanced.tags && {
          tags: Array.isArray(enhanced.tags) ? enhanced.tags : [],
        }),
        ...(enhanced.suggestions && {
          suggestions: Array.isArray(enhanced.suggestions)
            ? enhanced.suggestions
            : [],
        }),
      };
    } catch (error: any) {
      console.error('Error enhancing task:', error);
      throw new Error(
        `Failed to enhance task: ${error.message || 'Unknown error'}`
      );
    }
  }
}

export const assistantService = new AssistantService();
